import React, { useEffect, useMemo, useState } from 'react'
import { CalendarDays, AlertTriangle, Cloud, DollarSign } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import DataTable from '../../components/DataTable/DataTable'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import subscriptionService from '../../services/subscriptionService'
import {
  EXPIRY_WINDOW_OPTIONS,
  defaultSubscriptionStats,
  daysRemainingClass,
  formatCurrency,
  formatDate,
  getCriticalityClass,
} from './subscriptionUtils'
import '../../styles/subscriptions-tables.css'

const ExpirationList = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(defaultSubscriptionStats())
  const [windowDays, setWindowDays] = useState(30)
  const [renewItem, setRenewItem] = useState(null)
  const [showRenewModal, setShowRenewModal] = useState(false)

  useEffect(() => {
    loadData()
  }, [windowDays])

  const loadData = async () => {
    try {
      setLoading(true)
      const params = windowDays < 0
        ? { days: 3650, includeExpired: 'true', to: new Date().toISOString() }
        : { days: windowDays }
      const [listRes, statsRes] = await Promise.all([
        subscriptionService.getExpirations(params),
        subscriptionService.getStats(),
      ])
      if (listRes.success) {
        let rows = listRes.data || []
        if (windowDays < 0) {
          rows = rows.filter((r) => (r.daysRemaining ?? 0) < 0)
        }
        setData(rows.map((r, i) => ({ ...r, id: `${r.subscriptionId}-${r.kind}-${i}` })))
      }
      if (statsRes.success && statsRes.data) {
        setStats({ ...defaultSubscriptionStats(), ...statsRes.data })
      }
    } catch (error) {
      if (!error._errorHandled) toast.error('Failed to load expirations')
    } finally {
      setLoading(false)
    }
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'subscriptionNumber',
      header: 'Subscription #',
      cell: ({ getValue }) => <span className="code-text">{getValue() || '-'}</span>,
    },
    {
      accessorKey: 'subscriptionName',
      header: 'Subscription / resource',
      cell: ({ row }) => (
        <div>
          <div className="description-text">{row.original.subscriptionName}</div>
          <div style={{ fontSize: 12, color: '#5B6478' }}>
            {row.original.resourceName ? `${row.original.label}: ${row.original.resourceName}` : row.original.label}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'vendorName',
      header: 'Vendor',
      cell: ({ getValue }) => getValue() || '—',
    },
    {
      accessorKey: 'kind',
      header: 'Expiry type',
      cell: ({ row }) => row.original.label || row.original.kind,
    },
    {
      accessorKey: 'date',
      header: 'Date',
      meta: { align: 'center' },
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      accessorKey: 'daysRemaining',
      header: 'Days left',
      meta: { align: 'center' },
      cell: ({ getValue }) => {
        const d = getValue()
        return <span className={`sub-days ${daysRemainingClass(d)}`}>{d}</span>
      },
    },
    {
      accessorKey: 'criticality',
      header: 'Criticality',
      meta: { align: 'center' },
      cell: ({ getValue }) => {
        const v = getValue()
        if (!v) return '—'
        return <span className={`sub-crit ${getCriticalityClass(v)}`}>{v}</span>
      },
    },
  ], [])

  const handleRenew = async () => {
    if (!renewItem?.subscriptionId) return
    try {
      const response = await subscriptionService.renew(renewItem.subscriptionId, { decision: 'Renew' })
      if (response.success) {
        toast.success('Subscription renewed')
        setShowRenewModal(false)
        setRenewItem(null)
        loadData()
      } else toast.error(response.message || 'Renewal failed')
    } catch (error) {
      if (!error._errorHandled) toast.error('Renewal failed')
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={[
        { label: 'Subscriptions', path: '/subscriptions', onClick: () => navigate('/subscriptions') },
        { label: 'Expirations', isCurrent: true },
      ]} />
      <div className="page-header">
        <div className="page-title">
          <h1>Expirations</h1>
          <p>Subscriptions and child resources approaching expiry, return or notice deadlines.</p>
        </div>
        <div className="page-actions">
          <select className="form-control" style={{ width: 220 }} value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))}>
            {EXPIRY_WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="dashboard-metrics-grid" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-icon"><CalendarDays size={16} /></div><div className="metric-value">{data.length}</div><div className="metric-title">In selected window</div></div>
        <div className="metric-card"><div className="metric-icon"><AlertTriangle size={16} /></div><div className="metric-value">{stats.expiring}</div><div className="metric-title">Expiring in 30 days</div></div>
        <div className="metric-card"><div className="metric-icon"><Cloud size={16} /></div><div className="metric-value">{stats.active}</div><div className="metric-title">Active subscriptions</div></div>
        <div className="metric-card"><div className="metric-icon"><DollarSign size={16} /></div><div className="metric-value">{formatCurrency(stats.monthlyCost)}</div><div className="metric-title">Monthly committed</div></div>
      </div>

      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search expirations…"
        onView={(row) => navigate(`/subscriptions/view/${row.subscriptionId}`)}
        customActions={[
          {
            label: 'Open',
            onClick: (row) => navigate(`/subscriptions/view/${row.subscriptionId}`),
          },
          {
            label: 'Complete renewal',
            onClick: (row) => { setRenewItem(row); setShowRenewModal(true) },
          },
        ]}
      />

      <ConfirmationModal
        isOpen={showRenewModal}
        onClose={() => { setShowRenewModal(false); setRenewItem(null) }}
        onConfirm={handleRenew}
        title="Complete renewal"
        message={`Complete renewal for ${renewItem?.subscriptionName || 'this subscription'}?`}
        confirmText="Complete renewal"
      />
    </div>
  )
}

export default ExpirationList
